import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Skeleton,
} from "@mui/material";
import { useDispatch } from "react-redux";
import {
  generateMcpApiKey,
  listMcpApiKeys,
  McpApiKey,
  revokeMcpApiKey,
} from "api";
import { useTranslations } from "hooks";
import { getTranslation } from "common";
import ShowApiKeyOnceModal from "./ShowApiKeyOnceModal";

interface McpApiKeysSectionProps {
  /**
   * The Profile page renders a disabled Switch that mirrors whether the
   * user has at least one active API key. We compute that here (the only
   * place that has the keys) and bubble it up via this callback.
   */
  onHasActiveChange?: (hasActive: boolean) => void;
}

/**
 * Inline section under the user data form on the Profile page. Lets the
 * user generate new MCP API keys, view the metadata of existing ones, and
 * revoke them. The full key is only ever shown once, in the show-once modal.
 */
const McpApiKeysSection: React.FC<McpApiKeysSectionProps> = ({
  onHasActiveChange,
}) => {
  const translations = useTranslations();
  const dispatch = useDispatch();
  const [keys, setKeys] = useState<McpApiKey[]>([]);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listMcpApiKeys(true);
      setKeys(data);
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ||
          e?.message ||
          "Failed to load MCP API keys"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Bubble the derived "has at least one active key" up to the parent so the
  // disabled Switch stays in sync. The switch is a read-only reflection —
  // see plan §3.2 (the flag does not exist in DB, by design).
  useEffect(() => {
    if (onHasActiveChange) {
      onHasActiveChange(keys.some((k) => !k.revoked_at));
    }
  }, [keys, onHasActiveChange]);

  const handleGenerate = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setError(null);
    try {
      const result = await generateMcpApiKey(trimmed, ["read", "write"]);
      setRevealedKey(result.token);
      setName("");
      await reload();
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ||
          e?.message ||
          "Failed to generate MCP API key"
      );
    }
  };

  const handleRevoke = async (id: number) => {
    setError(null);
    try {
      await revokeMcpApiKey(id);
      await reload();
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ||
          e?.message ||
          "Failed to revoke MCP API key"
      );
    }
  };

  const t = (key: string, fallback: string) =>
    getTranslation(translations, key) || fallback;

  return (
    <Box mt={4}>
      <Typography variant="h6" gutterBottom>
        {t("profile.mcp.section.title", "API keys MCP")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t(
          "profile.mcp.section.description",
          "MCP API keys let external clients (e.g. Claude Desktop) access your Flowenti tools. Each key is tied to a client name you choose."
        )}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 3 }}>
        <TextField
          size="small"
          fullWidth
          label={t(
            "profile.mcp.name.label",
            "Name (e.g. Claude Desktop - MacBook Fabio)"
          )}
          value={name}
          onChange={(e) => setName(e.target.value)}
          inputProps={{ maxLength: 100, id: "profile-mcp-name-input" }}
          disabled={loading}
        />
        <Button
          variant="contained"
          disabled={!name.trim() || loading}
          onClick={handleGenerate}
          id="profile-mcp-generate-btn"
          sx={{ minWidth: 120 }}
        >
          {t("profile.mcp.generate", "Generate")}
        </Button>
      </Stack>

      {loading && keys.length === 0 ? (
        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
      ) : (
        <TableContainer>
          <Table size="small" id="profile-mcp-keys-table">
            <TableHead>
              <TableRow>
                <TableCell>{t("profile.mcp.col.name", "Name")}</TableCell>
                <TableCell>{t("profile.mcp.col.client", "Client")}</TableCell>
                <TableCell>{t("profile.mcp.col.scopes", "Scopes")}</TableCell>
                <TableCell>{t("profile.mcp.col.status", "Status")}</TableCell>
                <TableCell>
                  {t("profile.mcp.col.lastSeen", "Last seen")}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {keys.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 2 }}
                    >
                      {t(
                        "profile.mcp.empty",
                        "No MCP API keys yet. Generate your first one above."
                      )}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {keys.map((k) => (
                <TableRow key={k.id} hover>
                  <TableCell>{k.name}</TableCell>
                  <TableCell>
                    <code style={{ fontSize: 12 }}>{k.client_type}</code>
                  </TableCell>
                  <TableCell>{k.scopes.join(", ")}</TableCell>
                  <TableCell>
                    {k.revoked_at
                      ? t("profile.mcp.revoked", "revoked")
                      : t("profile.mcp.active", "active")}
                  </TableCell>
                  <TableCell>
                    {k.last_seen_at
                      ? new Date(k.last_seen_at).toLocaleString()
                      : "—"}
                  </TableCell>
                  <TableCell align="right">
                    {!k.revoked_at && (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleRevoke(k.id)}
                        id={`profile-mcp-revoke-btn-${k.id}`}
                      >
                        {t("profile.mcp.revoke", "Revoke")}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ShowApiKeyOnceModal
        apiKey={revealedKey}
        onClose={() => setRevealedKey(null)}
      />
    </Box>
  );
};

export default McpApiKeysSection;
